import ConfirmationModal from 'components/Modals/ConfirmationModal';
import type { CSSProperties } from 'react';
import { useSelector } from 'react-redux';
import { selectIpawsHoldFlag } from '../../../../state/selectors/alertCreatorSelectors';
import type { IpawsPreviewModel } from '../../../../utils/ipawsSubmission.utils';

const previewModalStyle = {
  '--bs-modal-color': 'var(--bs-white)',
} as CSSProperties;

const previewContentStyle = {
  color: 'var(--bs-white)',
} as CSSProperties;

type IpawsSubmissionPreviewModalProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  previewModel: IpawsPreviewModel;
};

const IpawsSubmissionPreviewModal = ({
  show,
  onClose,
  onConfirm,
  isSubmitting,
  previewModel,
}: IpawsSubmissionPreviewModalProps) => {
  // Raised before a submit, released by the HTTP answer or the workplace event (FE-03).
  const ipawsHoldFlag = useSelector(selectIpawsHoldFlag);
  const isLiveEnvironment = previewModel.environment === 'PRODUCTION';
  const bodyTxt = (
    <div className="text-start" style={previewContentStyle}>
      {isLiveEnvironment ? (
        <div className="alert alert-danger" role="alert">
          <strong>LIVE IPAWS transmission.</strong> Confirming will send this alert to the public
          through the selected delivery channels.
        </div>
      ) : null}
      <p className="mb-2">
        <strong>{previewModel.destinationName}</strong>
      </p>
      <p className="mb-2">
        <strong>Environment:</strong> {previewModel.environmentLabel}
      </p>
      <p className="mb-2">
        <strong>COG:</strong> {previewModel.cogDisplayId}
      </p>
      <p className="mb-2">
        <strong>Issuing Authority:</strong> {previewModel.issuingAuthority || 'Not provided'}
      </p>
      <p className="mb-2">
        <strong>Credential Status:</strong> {previewModel.credentialStatus}
      </p>
      {previewModel.dissemination ? <p className="mb-2">{previewModel.dissemination}</p> : null}
      <details className="mb-2">
        <summary>Advanced FEMA COG details</summary>
        <div className="mt-1">FEMA COG ID: {previewModel.cogId || 'Not provided'}</div>
        <div>FEMA COG Name: {previewModel.cogName || 'Not provided'}</div>
      </details>
      <p className="mb-2">
        <strong>Event:</strong> {previewModel.event || 'Missing'} ({previewModel.eventCode || '---'}
        )
      </p>
      <div className="mb-2">
        <strong>CAP categories:</strong>{' '}
        {previewModel.categories.length ? (
          <ul className="mb-0 mt-1">
            {previewModel.categories.map((category) => (
              <li key={category}>{category}</li>
            ))}
          </ul>
        ) : (
          'Missing'
        )}
      </div>
      <p className="mb-2">
        <strong>Delivery channels:</strong>{' '}
        {previewModel.deliveryChannels.length
          ? previewModel.deliveryChannels.join(', ')
          : 'Missing'}
      </p>
      <p className="mb-2">
        <strong>Affected Area:</strong> {previewModel.areaDescription || 'Missing'}
      </p>
      <p className="mb-2">
        <strong>Expires:</strong> {previewModel.expiresAtLabel}
      </p>
      <p className="mb-2">
        <strong>Headline:</strong> {previewModel.headline || 'Missing'}
      </p>
      <p className="mb-0">
        <strong>Description:</strong> {previewModel.description || 'Missing'}
      </p>
      {previewModel.web ? (
        <p className="mb-0 mt-2">
          <strong>Public information URL:</strong> {previewModel.web}
        </p>
      ) : null}
      {previewModel.contact ? (
        <p className="mb-0 mt-2">
          <strong>Public contact:</strong> {previewModel.contact}
        </p>
      ) : null}
    </div>
  );

  return (
    <ConfirmationModal
      show={show}
      handleShow={onClose}
      backgroundColor="var(--color-primary-shadow)"
      style={previewModalStyle}
      backdrop="static"
      keyboard={false}
      title={isLiveEnvironment ? 'Confirm LIVE IPAWS Alert' : 'IPAWS Broadcast Preview'}
      aria-label={isLiveEnvironment ? 'Confirm LIVE IPAWS Alert' : 'IPAWS Broadcast Preview'}
      size="md"
      bodyTxt={bodyTxt}
      cancelBtnTxt="Cancel"
      confirmBtnTxt={
        isSubmitting || ipawsHoldFlag
          ? 'Publishing...'
          : isLiveEnvironment
            ? 'Send LIVE IPAWS Alert'
            : 'Publish to IPAWS'
      }
      cancelHandler={onClose}
      confirmHandler={() => {
        if (!isSubmitting && !ipawsHoldFlag) onConfirm();
      }}
      confirmButtonVariant="success"
      customBodyTxtClassName="fs-6 w-100 mb-3"
    />
  );
};

export default IpawsSubmissionPreviewModal;
